import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { staffProfileApi, ApiError, STORAGE_KEYS } from '../api';
import type { MyInfoRequest, MyInfoRequestState } from '../types/staffInfoRequest';

/**
 * In-app notifications for the signed-in user.
 *
 * The personal-info request is the first (and currently only) feeder. New
 * notification sources should add their own slice of state plus an entry in
 * `selectNotifications`, so the bell keeps rendering a single generic list.
 *
 * SECURITY: never put personal information in this store. It runs through the
 * `devtools` middleware, which streams state into the Redux DevTools extension
 * where it is readable and persisted in the action log. The SSN, DOB and address
 * collected by the info-request form stay in local component state and are
 * dropped on submit — only the status booleans below ever land here.
 */

export type NotificationId = 'info-request';

export interface AppNotification {
	id: NotificationId;
	severity: 'warning' | 'info';
	title: string;
	message: string;
	actionLabel?: string;
}

/**
 * The API reports three independent booleans rather than an enum, so collapse
 * them with an explicit precedence. `completed` is terminal and wins outright;
 * `expired` beats `pending` because a request past its expiry is expired even if
 * the server still flags it pending.
 *
 * TODO: confirm with backend whether these are mutually exclusive — if they are,
 * this precedence is belt-and-braces and can be simplified.
 */
export const deriveInfoRequestState = (request: MyInfoRequest | null): MyInfoRequestState => {
	if (!request) return 'NONE';
	if (request.completed) return 'COMPLETED';
	if (request.expired) return 'EXPIRED';
	if (request.pending) return 'PENDING';
	return 'NONE';
};

const readDismissed = () => {
	try {
		return sessionStorage.getItem(STORAGE_KEYS.INFO_REQUEST_DISMISSED) === 'true';
	} catch {
		return false; // private mode / storage disabled — just show the prompt
	}
};

interface NotificationsState {
	infoRequest: MyInfoRequest | null;
	infoRequestState: MyInfoRequestState;
	isLoadingInfoRequest: boolean;
	infoRequestError: string | null;
	/** "Remind me Later" for this session. Not persisted beyond it. */
	isInfoRequestDismissed: boolean;
	/** Modal visibility — shared so the bell and the auto-open gate agree. */
	isInfoRequestModalOpen: boolean;

	fetchInfoRequest: () => Promise<void>;
	openInfoRequestModal: () => void;
	dismissInfoRequest: () => void;
	markInfoRequestCompleted: () => void;
	clearNotifications: () => void;
}

const useNotificationsStore = create<NotificationsState>()(
	devtools(
		set => ({
			infoRequest: null,
			infoRequestState: 'NONE',
			isLoadingInfoRequest: false,
			infoRequestError: null,
			isInfoRequestDismissed: readDismissed(),
			isInfoRequestModalOpen: false,

			fetchInfoRequest: async () => {
				set({ isLoadingInfoRequest: true, infoRequestError: null });

				try {
					const infoRequest = await staffProfileApi.getMyInfoRequest();

					set({
						infoRequest,
						infoRequestState: deriveInfoRequestState(infoRequest),
						isLoadingInfoRequest: false,
					});
				} catch (error) {
					const apiError = error as ApiError;

					// A 404 just means nothing was ever requested of this staff member.
					if (error instanceof ApiError && error.isNotFound()) {
						set({
							infoRequest: null,
							infoRequestState: 'NONE',
							isLoadingInfoRequest: false,
						});
						return;
					}

					set({
						infoRequestError: apiError.message || 'Failed to load notifications',
						isLoadingInfoRequest: false,
					});

					if (import.meta.env.DEV) {
						// eslint-disable-next-line no-console
						console.error('Info-request fetch error:', apiError);
					}
				}
			},

			openInfoRequestModal: () => {
				set({ isInfoRequestModalOpen: true });
			},

			// Closing the modal always defers it for the session — "Remind me Later"
			// and the backdrop/close affordances mean the same thing here.
			dismissInfoRequest: () => {
				try {
					sessionStorage.setItem(STORAGE_KEYS.INFO_REQUEST_DISMISSED, 'true');
				} catch {
					// Non-fatal: the prompt just reappears on the next mount.
				}
				set({ isInfoRequestDismissed: true, isInfoRequestModalOpen: false });
			},

			markInfoRequestCompleted: () => {
				set(state => ({
					infoRequest: state.infoRequest
						? { ...state.infoRequest, pending: false, expired: false, completed: true }
						: null,
					infoRequestState: 'COMPLETED',
					isInfoRequestModalOpen: false,
				}));
			},

			clearNotifications: () => {
				try {
					sessionStorage.removeItem(STORAGE_KEYS.INFO_REQUEST_DISMISSED);
				} catch {
					// Non-fatal.
				}
				set({
					infoRequest: null,
					infoRequestState: 'NONE',
					isLoadingInfoRequest: false,
					infoRequestError: null,
					isInfoRequestDismissed: false,
					isInfoRequestModalOpen: false,
				});
			},
		}),
		{ name: 'notifications-store' }
	)
);

/**
 * Collapses every notification source into the list the bell renders.
 *
 * Deliberately a plain function of primitives rather than a zustand selector:
 * a selector returning a fresh array would fail zustand v5's identity check on
 * every render and loop. Callers subscribe to the primitives and useMemo this.
 */
export const buildNotifications = (
	infoRequestState: MyInfoRequestState,
	businessName: string | null
): AppNotification[] => {
	const notifications: AppNotification[] = [];

	if (infoRequestState === 'PENDING') {
		notifications.push({
			id: 'info-request',
			severity: 'warning',
			title: 'Personal information requested',
			message: businessName
				? `${businessName} needs your employment and payroll details.`
				: 'Your administrator needs your employment and payroll details.',
			actionLabel: 'Complete Now',
		});
	}

	if (infoRequestState === 'EXPIRED') {
		notifications.push({
			id: 'info-request',
			severity: 'warning',
			title: 'Information request expired',
			message: 'Ask your administrator to send a new request.',
		});
	}

	return notifications;
};

export default useNotificationsStore;
